import { clientConfig } from '@/config/clientConfig';
import { getJobDocumentDownloadUrl } from '@/domains/jobs/api/jobDocumentsApi';
import { useJobActivityLog } from '@/domains/jobs/hooks/useJobActivityLog';
import type { JobData } from '@/domains/jobs/hooks/useJobData';
import { useJobDocuments } from '@/domains/jobs/hooks/useJobDocuments';
import { useUploadJobDocument } from '@/domains/jobs/hooks/useUploadJobDocument';
import { describeAuditEvent } from '@/domains/settings';
import { FileDropzone, SelectedFilesList } from '@/shared/components';
import { formatMinutesCompact } from '@/shared/lib/duration-utils';
import { DOCUMENT_FILE_TYPES } from '@/shared/lib/fileTypes';
import { formatFileSize } from '@/shared/lib/formatFileSize';
import { showErrorToast, showSuccessToast } from '@/shared/notifications/toast-utils';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import type { AuditTrailItem, RawCommunicationLogRow } from '@skewb-works/contracts';
import {
  CheckCircle2,
  Download,
  Eye,
  FileText,
  HelpCircle,
  History,
  Loader2,
  PencilLine,
  Upload,
  XCircle,
} from 'lucide-react';
import { useState } from 'react';
import { ActivityLogItem } from './ActivityLogItem';

// ============================================================================
// ACTIVITY LOG TAB
// ============================================================================

/**
 * SC-6369: `audit.*` events come from `audit_trail` — changes made inside Works
 * Lite. They used to be invisible here because the tab only read the CORE
 * `communication_log`.
 */
export const EVENT_LABELS: Record<string, string> = {
  'audit.field_update': 'Job details updated',
  'audit.status_change': 'Status changed',
  'audit.assignment': 'Assignment changed',
  'audit.completion': 'Job completed',
  'audit.authority_override': 'Authority override',
  'audit.creation': 'Created',
  'audit.deletion': 'Deleted',
  'work_order.new': 'Received job',
  'work_order.accepted': 'Accepted job',
  'work_order.rejected': 'Rejected job',
  'work_order.more_info_requested': 'Info requested',
  'work_order.more_info_received': 'Info received',
  'work_order.scheduled': 'Scheduled job',
  'work_order.unscheduled': 'Job unscheduled',
  'work_order.duration_challenge': 'Duration challenge raised',
  'work_order.duration_challenge_approved': 'Duration challenge response',
  'work_order.engineer_en_route': 'Engineer en route',
  'work_order.engineer_on_site': 'Engineer on site',
  'work_order.aborted': 'Job aborted',
  'work_order.engineering_complete': 'Engineering complete',
  'work_order.completed': 'Job completed',
  'work_order.update': 'Job updated',
  'work_order.validated': 'Job validated',
  'work_order.validation_requested': 'Validation requested',
};

export function getIcon(eventType: string) {
  // Works Lite edits get their own tone so they read as distinct from the CORE
  // event stream they're interleaved with (SC-6369).
  if (eventType.startsWith('audit.')) {
    if (eventType === 'audit.deletion') {
      return { icon: XCircle, color: 'text-red-600', bgColor: 'bg-red-50 dark:bg-red-950' };
    }
    return {
      icon: PencilLine,
      color: 'text-orange-600',
      bgColor: 'bg-orange-50 dark:bg-orange-950',
    };
  }
  if (
    [
      'work_order.accepted',
      'work_order.completed',
      'work_order.engineering_complete',
      'work_order.validated',
    ].includes(eventType)
  ) {
    return {
      icon: CheckCircle2,
      color: 'text-green-600',
      bgColor: 'bg-green-50 dark:bg-green-950',
    };
  }
  if (['work_order.rejected', 'work_order.aborted'].includes(eventType)) {
    return { icon: XCircle, color: 'text-red-600', bgColor: 'bg-red-50 dark:bg-red-950' };
  }
  if (['work_order.more_info_requested', 'work_order.duration_challenge'].includes(eventType)) {
    return { icon: HelpCircle, color: 'text-amber-600', bgColor: 'bg-amber-50 dark:bg-amber-950' };
  }
  return { icon: History, color: 'text-blue-600', bgColor: 'bg-blue-50 dark:bg-blue-950' };
}

// CNTME-1934: render invoice details on the activity log. The invoice may sit
// nested (workOrder.invoice, newer payloads) or top-level (payload.invoice,
// older ones) — read both so existing log rows still show it.
export function extractInvoiceLine(payload: unknown): string | null {
  const p = payload as any;
  const invoice = p?.workOrder?.invoice ?? p?.invoice;
  if (!invoice || typeof invoice !== 'object') return null;
  const parts: string[] = [];
  if (invoice.reference) parts.push(String(invoice.reference));
  if (
    invoice.amount !== undefined &&
    invoice.amount !== null &&
    !Number.isNaN(Number(invoice.amount))
  )
    parts.push(
      new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(
        Number(invoice.amount),
      ),
    );
  if (!parts.length) return null;
  return `Invoice: ${parts.join(' • ')}`;
}

/**
 * Turns an `audit.*` row's payload back into the shape `describeAuditEvent`
 * expects, so a Works Lite edit is described here exactly as it is on the user
 * Log tab and the global audit trail — one wording, one place to change it.
 * Returns null for anything that isn't an audit row (SC-6369).
 */
export function extractAuditMessage(payload: unknown, eventType: string): string | null {
  if (!eventType.startsWith('audit.')) return null;
  const p = (payload ?? {}) as Record<string, unknown>;
  return describeAuditEvent({
    event_type: eventType.slice('audit.'.length),
    field_name: (p.fieldName as string | null) ?? null,
    previous_value: p.previousValue ?? null,
    new_value: p.newValue ?? null,
    reason: (p.reason as string | null) ?? null,
  } as AuditTrailItem);
}

export function extractMessage(payload: unknown, eventType: string): string {
  const auditMessage = extractAuditMessage(payload, eventType);
  if (auditMessage) return auditMessage;

  const p = payload as any;
  const invoiceLine = extractInvoiceLine(payload);
  const notes = p?.workOrder?.notes;
  if (Array.isArray(notes) && notes.length > 0) {
    const text = notes
      .map((n: any) => n.note || '')
      .filter(Boolean)
      .join('\n');
    if (text) {
      // Avoid duplicating the invoice when it's already embedded as a note.
      return invoiceLine && !text.includes(invoiceLine) ? `${text}\n${invoiceLine}` : text;
    }
  }
  if (p?.schedule) {
    const s = p.schedule;
    const parts: string[] = [];
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (s.duration) parts.push(`Duration: ${formatMinutesCompact(Number(s.duration))}`);
    if (s.windowStartDate)
      parts.push(`Start: ${new Date(s.windowStartDate).toLocaleString('en-GB', { timeZone: tz })}`);
    if (s.windowEndDate)
      parts.push(`End: ${new Date(s.windowEndDate).toLocaleString('en-GB', { timeZone: tz })}`);
    if (parts.length) return parts.join(' • ');
  }
  if (invoiceLine) return invoiceLine;
  return EVENT_LABELS[eventType] ?? eventType.replace('work_order.', '').replace(/_/g, ' ');
}

export function extractActor(
  payload: unknown,
  direction: 'inbound' | 'outbound',
  // Audit rows carry the acting user on the row itself rather than in the
  // payload — show the person who made the change, not a system label (SC-6369).
  actor?: string | null,
): string {
  if (actor) return actor;
  const p = payload as any;
  const notes = p?.workOrder?.notes;
  if (Array.isArray(notes) && notes.length > 0 && notes[0].createdBy) {
    return notes[0].createdBy;
  }
  if (p?.meta?.source) return p.meta.source;
  // SC-6258: fall back to the tenant-configured source label so brands that
  // relabel a source (e.g. YW: 'core' → 'System') stay consistent with the
  // Messages tab instead of showing the hardcoded "CORE Cockpit".
  const sender = direction === 'outbound' ? 'skewbworks' : 'core';
  return clientConfig.messageSources[sender].label;
}

export function ActivityLogTab({ jobId, jobData: _jobData }: { jobId: string; jobData: JobData }) {
  const { data: rows, isLoading } = useJobActivityLog(Number(jobId));

  const activityLog: RawCommunicationLogRow[] = rows ?? [];

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#E84A2B]" />
            <span className="ml-3 text-muted-foreground text-sm">Loading activity log...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-slate-50 dark:bg-slate-900 flex items-center justify-center">
            <History className="w-5 h-5 text-slate-600 dark:text-slate-400" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">Activity Log</h3>
            <p className="text-xs text-muted-foreground">
              Complete history of job changes and updates
            </p>
          </div>
        </div>

        {activityLog.length === 0 ? (
          <div className="text-center py-12">
            <History className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-foreground mb-2">No Activity Yet</h3>
            <p className="text-sm text-muted-foreground">
              Activity will appear here as changes are made to this job.
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {activityLog.map((row, index) => (
              <ActivityLogItem
                // Ids are unique per source table, not across both, so the kind
                // has to be part of the key (SC-6369).
                key={`${row.kind}:${row.id}`}
                row={row}
                isLast={index === activityLog.length - 1}
                jobId={Number(jobId)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// DOCUMENTS TAB
// ============================================================================

export function DocumentsTab({ jobId }: { jobId: string }) {
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadFiles, setUploadFiles] = useState<File[]>([]);
  const [uploadReference, setUploadReference] = useState('');

  const numericJobId = Number(jobId);
  const { data, isLoading } = useJobDocuments(numericJobId);
  const uploadMutation = useUploadJobDocument(numericJobId);

  // Tracks which document action is in flight, e.g. `"12:view"` or `"12:download"`,
  // so we can disable just that button and show a spinner on it.
  const [pendingAction, setPendingAction] = useState<string | null>(null);

  const documents = data?.data ?? [];

  const handleDocumentAction = async (documentId: number, disposition: 'inline' | 'attachment') => {
    const actionKey = `${documentId}:${disposition}`;
    setPendingAction(actionKey);
    try {
      const { url } = await getJobDocumentDownloadUrl(numericJobId, documentId, disposition);
      // The presigned URL already carries the right Content-Disposition, so opening
      // it in a new tab views (inline) or downloads (attachment) without navigating away.
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch {
      showErrorToast(
        disposition === 'inline' ? 'Unable to open document' : 'Unable to download document',
        'Please try again.',
      );
    } finally {
      setPendingAction(null);
    }
  };

  const getCategoryBadge = (fileCategory: number | null) => {
    switch (fileCategory) {
      case 1:
        return (
          <Badge
            variant="outline"
            className="bg-blue-50 text-blue-700 border-blue-600 dark:bg-blue-950/30"
          >
            Survey
          </Badge>
        );
      case 2:
        return (
          <Badge
            variant="outline"
            className="bg-red-50 text-red-700 border-red-600 dark:bg-red-950/30"
          >
            Safety
          </Badge>
        );
      case 3:
        return (
          <Badge
            variant="outline"
            className="bg-purple-50 text-purple-700 border-purple-600 dark:bg-purple-950/30"
          >
            Legal
          </Badge>
        );
      case 4:
        return (
          <Badge
            variant="outline"
            className="bg-orange-50 text-orange-700 border-orange-600 dark:bg-orange-950/30"
          >
            Technical
          </Badge>
        );
      case 5:
        return (
          <Badge
            variant="outline"
            className="bg-green-50 text-green-700 border-green-600 dark:bg-green-950/30"
          >
            Media
          </Badge>
        );
      default:
        return <Badge variant="outline">Other</Badge>;
    }
  };

  const handleFilesAccepted = (accepted: File[]) => {
    setUploadFiles((prev) => {
      const seen = new Set(prev.map((f) => `${f.name}:${f.size}`));
      return [...prev, ...accepted.filter((f) => !seen.has(`${f.name}:${f.size}`))];
    });
  };

  const handleFileRemove = (index: number) => {
    setUploadFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const closeUploadModal = () => {
    setShowUploadModal(false);
    setUploadFiles([]);
    setUploadReference('');
  };

  const handleUpload = async () => {
    if (uploadFiles.length === 0) return;
    try {
      for (const file of uploadFiles) {
        await uploadMutation.mutateAsync({ file });
      }
      showSuccessToast(
        uploadFiles.length === 1 ? 'Document uploaded' : `${uploadFiles.length} documents uploaded`,
      );
      closeUploadModal();
    } catch {
      // Leave the modal open — global mutation error handler shows the toast.
    }
  };

  if (isLoading) {
    return (
      <div className="max-w-6xl mx-auto">
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#E84A2B]" />
            <span className="ml-3 text-muted-foreground text-sm">Loading documents...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="max-w-6xl mx-auto">
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-orange-50 dark:bg-orange-950 flex items-center justify-center">
                <FileText className="w-5 h-5 text-orange-600 dark:text-orange-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground">Documents</h3>
                <p className="text-xs text-muted-foreground">
                  {documents.length} file{documents.length !== 1 ? 's' : ''} attached to this job
                </p>
              </div>
            </div>
            <Button
              size="sm"
              className="bg-[#E84A2B] hover:bg-[#E84A2B]/90 text-white"
              onClick={() => setShowUploadModal(true)}
            >
              <Upload className="w-4 h-4 mr-2" />
              Upload Document
            </Button>
          </div>

          {documents.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-foreground mb-2">No Documents Yet</h3>
              <p className="text-sm text-muted-foreground">
                Upload documents to attach them to this job.
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {documents.map((doc) => (
                <div
                  key={doc.id}
                  className="flex items-center justify-between p-4 rounded-lg bg-muted/50 border border-border hover:bg-muted/70 transition-colors group"
                >
                  <div className="flex items-center gap-4 flex-1 min-w-0">
                    <div className="w-10 h-10 rounded-lg bg-background border border-border flex items-center justify-center flex-shrink-0">
                      <FileText className="w-5 h-5 text-muted-foreground" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-foreground text-sm truncate">
                        {doc.name ?? 'Unnamed Document'}
                      </div>
                      <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                        {doc.type && (
                          <>
                            <span>{doc.type}</span>
                            <span>•</span>
                          </>
                        )}
                        {doc.created_by && (
                          <>
                            <span>Uploaded by {doc.created_by}</span>
                            <span>•</span>
                          </>
                        )}
                        {doc.created_time && (
                          <span>
                            {new Date(doc.created_time).toLocaleDateString('en-GB', {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric',
                            })}
                          </span>
                        )}
                        {formatFileSize(doc.file_size) && (
                          <>
                            <span>•</span>
                            <span>{formatFileSize(doc.file_size)}</span>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {getCategoryBadge(doc.file_category)}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 ml-4">
                    <Button
                      variant="ghost"
                      size="sm"
                      title="View document"
                      aria-label="View document"
                      disabled={pendingAction === `${doc.id}:inline`}
                      onClick={() => handleDocumentAction(doc.id, 'inline')}
                    >
                      {pendingAction === `${doc.id}:inline` ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Eye className="w-4 h-4" />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      title="Download document"
                      aria-label="Download document"
                      disabled={pendingAction === `${doc.id}:attachment`}
                      onClick={() => handleDocumentAction(doc.id, 'attachment')}
                    >
                      {pendingAction === `${doc.id}:attachment` ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Download className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Upload Modal */}
      <Dialog
        open={showUploadModal}
        onOpenChange={(open) => {
          // Only react to close requests; the built-in close (X) button and any
          // programmatic close funnel through here so upload state is reset.
          if (!open && !uploadMutation.isPending) closeUploadModal();
        }}
      >
        <DialogContent
          className="w-96"
          // Block accidental dismissal: clicking the backdrop or pressing Escape
          // must not close the dialog and discard selected files (WRK-5564).
          onInteractOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle>Upload Document</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <FileDropzone
              preset={DOCUMENT_FILE_TYPES}
              multiple
              disabled={uploadMutation.isPending}
              onFilesAccepted={handleFilesAccepted}
            />
            <SelectedFilesList
              files={uploadFiles}
              onRemove={handleFileRemove}
              disabled={uploadMutation.isPending}
            />
            <Input
              type="text"
              placeholder="Reference"
              value={uploadReference}
              onChange={(e) => setUploadReference(e.target.value)}
              className="border border-border rounded-lg p-2"
            />
          </div>
          <DialogFooter>
            <Button
              size="sm"
              variant="outline"
              onClick={closeUploadModal}
              disabled={uploadMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              className="bg-[#E84A2B] hover:bg-[#E84A2B]/90 text-white"
              onClick={handleUpload}
              disabled={uploadMutation.isPending || uploadFiles.length === 0}
            >
              {uploadMutation.isPending ? 'Uploading…' : 'Upload'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
